import { Config } from '../../types/config';
import { Background, SearchLabel, SummaryTrigger } from './summary.styles';
import { useApplication } from '../../hooks/useApplication';
import { useBlanket } from '../../hooks/useBlanket';
import { useMediaQuery, BreakPointToken } from '@vaad1st/hook.media-query';
import { useTheme } from '@emotion/react';

import { ContainerComponent } from '@vaad1st/component.layout.container';
import SummaryTickerComponent from '../summary-ticker/summary-ticker.component';
import SummaryPlaybackComponent from '../summary-playback/summary-playback.component';
import {
	IconActionSearchComponent,
	IconsSize
} from '@vaad1st/component.atom.icons';
import CriteriaService from '../../lib/utils/criteria.service';
import { TypographyComponent } from '@vaad1st/component.atom.typography';
import type { Ref } from 'react';

interface SummaryComponentProps {
	config: Config;
	triggerRef?: Ref<HTMLButtonElement>;
	onOpen?: () => void;
	controlsId?: string;
}

const SummaryComponent = ({
	config,
	triggerRef,
	onOpen,
	controlsId
}: SummaryComponentProps) => {
	const { application, setApplication } = useApplication();
	const { blanket, setBlanket } = useBlanket();
	const theme = useTheme();

	const openExperience = () => {
		onOpen?.();
		setBlanket({ ...blanket, show: true });
		setApplication((prev) => ({ ...prev, open: true }));
	};

	const { summary: summaryConfig } = config;
	const hasSummary = CriteriaService.validateCriteriaCanShowPlayback(
		application?.criteria
	);

	const isMidSize = useMediaQuery(`(min-width: ${BreakPointToken.MdMin}px)`);

	return (
		<Background
			hasSummary={hasSummary}
			float={application?.float}
			theme={theme}
			data-cy="summary-component"
			data-float={application?.float}
			role="region">
			<ContainerComponent as="section">
				<SummaryTrigger
					ref={triggerRef}
					data-cy="summary-trigger"
					onClick={() => openExperience()}
					aria-controls={controlsId}
					aria-expanded={application?.open}
					visible={!application?.open}
					tabIndex={application?.open ? -1 : 0}>
					{hasSummary ? (
						<SummaryPlaybackComponent />
					) : (
						<SummaryTickerComponent {...summaryConfig} />
					)}

					<SearchLabel data-cy="summary-search-label">
						<IconActionSearchComponent
							size={isMidSize ? IconsSize.Med : IconsSize.Sml}
						/>
						<TypographyComponent>
							{hasSummary ? summaryConfig.editButton : summaryConfig.button}
						</TypographyComponent>
					</SearchLabel>
				</SummaryTrigger>
			</ContainerComponent>
		</Background>
	);
};

export default SummaryComponent;
